import React, { useEffect } from "react";
import { Calendar, Instagram, Sparkles } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { INSTAGRAM_URL, ASSETS } from "../data/constants";

// Hardcoded events list — leave empty for "blank" state, or add objects to populate.
// Each event: { id, title: {en, gr}, date, time, description: {en, gr}, image? }
const EVENTS = [
    {
        id: "aens-official-party-26-june",
        title: {
            en: "AENS Official Party",
            gr: "AENS Official Party",
        },
        date: { en: "Friday 26 June 2026", gr: "Παρασκευή 26 Ιουνίου 2026" },
        time: "21:00",
        description: {
            en: "The official AENS summer party comes to AMOS — DJ set by X. Koleros & Panzer, beats by the sea from 21:00. Reserve early: +30 697 414 1414.",
            gr: "Το επίσημο AENS summer party έρχεται στο AMOS — DJ set από τους X. Koleros & Panzer, ρυθμοί δίπλα στη θάλασσα από τις 21:00. Κάντε κράτηση: +30 697 414 1414.",
        },
        image: "https://customer-assets.emergentagent.com/job_amos-beach-bar/artifacts/9aqkephb_WhatsApp%20Image%202026-06-25%20at%2007.38.10.jpeg",
    },
    {
        id: "friday-vibes-kostas-traikos",
        title: {
            en: "Friday Vibes — Kostas Traikos On Decks",
            gr: "Friday Vibes — Kostas Traikos On Decks",
        },
        date: { en: "Every Friday", gr: "Κάθε Παρασκευή" },
        time: "15:00",
        description: {
            en: "Sunset house & deep beats with Kostas Traikos On Decks. Friday afternoons by the Aegean — cocktails, sun and slow grooves from 15:00.",
            gr: "Sunset house & deep ρυθμοί με τον Kostas Traikos On Decks. Παρασκευή απόγευμα δίπλα στο Αιγαίο — κοκτέιλ, ήλιος και χαλαρά grooves από τις 15:00.",
        },
        image: "https://customer-assets.emergentagent.com/job_amos-beach-bar/artifacts/08ciq3w4_WhatsApp%20Image%202026-06-25%20at%2007.38.11.jpeg",
    },
    {
        id: "greek-night-28-june",
        title: {
            en: "Greek Night — Outdoor Barbeque",
            gr: "Ελληνική Βραδιά — Υπαίθριο BBQ",
        },
        date: { en: "Sunday 28 June 2026", gr: "Κυριακή 28 Ιουνίου 2026" },
        time: "21:00",
        description: {
            en: "A Greek night to remember — outdoor barbeque under the stars with live music by Basileia Thasitoy, Andreas Katogoydis and Baggelis Familias. After 21:00. Argonauton 8, Richa Nera, Myrina.",
            gr: "Μια αξέχαστη ελληνική βραδιά — υπαίθριο BBQ κάτω από τα αστέρια με ζωντανή μουσική από τη Βασιλεία Θασίτου, τον Ανδρέα Κατωγουδή και τον Βαγγέλη Φαμίλια. Μετά τις 21:00. Αργοναυτών 8, Ρηχά Νερά, Μύρινα.",
        },
        image: "https://customer-assets.emergentagent.com/job_amos-beach-bar/artifacts/abicldi9_WhatsApp%20Imasdsdsge%202026-06-25%20at%2007.38.11.jpeg",
    },
];

const EventsPage = () => {
    const { t, lang } = useLang();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div data-testid="events-page" className="pt-32 pb-24 bg-ivory min-h-screen">
            <section className="container-x text-center mb-12">
                <p className="eyebrow">{t("nav.events")}</p>
                <h1
                    data-testid="events-title"
                    className="font-accent italic font-light text-aegean-800 text-5xl md:text-6xl lg:text-7xl leading-[1.05] mt-4"
                >
                    {t("events.title")}
                </h1>
                <div className="mt-8 mx-auto h-px w-16 bg-aegean-300" />
                <p className="mt-8 text-ink/70 max-w-xl mx-auto text-base md:text-lg leading-[1.8] font-light">
                    {t("events.subtitle")}
                </p>
            </section>

            {EVENTS.length === 0 ? (
                // Empty state — elegant blank with Instagram CTA
                <section className="container-x">
                    <div className="max-w-3xl mx-auto bg-white border border-aegean-100 px-8 md:px-16 py-16 md:py-24 text-center relative overflow-hidden">
                        {/* decorative pattern */}
                        <div
                            className="absolute inset-0 opacity-[0.04] pointer-events-none"
                            style={{
                                backgroundImage:
                                    "radial-gradient(circle at 1px 1px, #1E8FA5 1px, transparent 0)",
                                backgroundSize: "28px 28px",
                            }}
                        />
                        <div className="relative">
                            <div className="mx-auto w-16 h-16 grid place-items-center rounded-full bg-aegean-50 text-aegean-500 mb-7">
                                <Sparkles size={26} strokeWidth={1.4} />
                            </div>
                            <h2 className="font-accent italic text-aegean-800 text-3xl md:text-4xl font-light">
                                {t("events.empty")}
                            </h2>
                            <p className="mt-6 text-ink/65 text-base md:text-lg leading-[1.85] font-light max-w-xl mx-auto">
                                {t("events.emptyBody")}
                            </p>
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="events-instagram-cta"
                                className="mt-10 btn-outline-dark inline-flex"
                            >
                                <Instagram size={16} /> {t("events.followCta")}
                            </a>
                        </div>
                    </div>

                    {/* Editorial image strip — gives the page visual weight */}
                    <div className="grid grid-cols-3 gap-2 max-w-4xl mx-auto mt-16 opacity-90">
                        {[
                            { src: ASSETS.venueEntrance, alt: "Amos Beach Bar seaside entrance with rattan chairs" },
                            { src: ASSETS.dessertCushion, alt: "Signature Amos dessert by the sea" },
                            { src: ASSETS.steakPlate, alt: "Grilled steak plate from the Amos kitchen" },
                        ].map((it, i) => (
                            <div key={i} className="aspect-square overflow-hidden">
                                <img src={it.src} alt={it.alt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1200ms]" />
                            </div>
                        ))}
                    </div>
                </section>
            ) : (
                // Card grid when events exist
                <section className="container-x">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {EVENTS.map((ev) => (
                            <article
                                key={ev.id}
                                data-testid={`event-card-${ev.id}`}
                                className="bg-white border border-aegean-100 shadow hover:shadow-xl transition-shadow flex flex-col overflow-hidden"
                            >
                                {ev.image && (
                                    <div className="aspect-[4/5] overflow-hidden bg-aegean-50">
                                        <img src={ev.image} alt={ev.title[lang] || ev.title.en} className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <div className="p-7 flex-1 flex flex-col">
                                    <div className="flex items-center gap-2 text-aegean-500 text-xs font-display uppercase tracking-[0.22em]">
                                        <Calendar size={13} />
                                        {(ev.date && (ev.date[lang] || ev.date.en || ev.date)) } · {ev.time}
                                    </div>
                                    <h3 className="mt-3 font-accent italic text-aegean-800 text-2xl md:text-3xl leading-tight">
                                        {ev.title[lang] || ev.title.en}
                                    </h3>
                                    <p className="mt-4 text-ink/70 text-sm leading-relaxed font-light flex-1">
                                        {ev.description[lang] || ev.description.en}
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
};

export default EventsPage;
